import { useState, useCallback } from "react";
import API from "@/api/api";

type FormState = { name: string; email: string; phone: string; service: string; budget: string; message: string; };
const initialForm: FormState = { name: "", email: "", phone: "", service: "", budget: "", message: "" };
const serviceOptions = ["Digital Marketing", "App Development", "AI Consulting", "IT Consulting & Staffing", "Cybersecurity & Cloud", "Big Data & Analytics", "Other"];
const budgetOptions = ["Under $5,000", "$5,000 – $20,000", "$20,000 – $50,000", "$50,000+", "Not sure yet"];

export default function ContactPage() {
  const [form, setForm] = useState<FormState>(initialForm);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const openAIAgent = useCallback(() => {
    const btn = document.querySelector<HTMLElement>("#vapi-widget-floating-button");
    btn?.click();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (form.message.length > 500) { setError("Message must be 500 characters or less."); return; }
    setError(""); setSubmitting(true);
    try {
      await API.post("/contact/", form);
      setForm(initialForm);

      setSubmitted(true);
    } catch (err: any) {

      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError("Unable to send your message.");
      }
  
    }
    finally { setSubmitting(false); }
  };

  return (
    <main className="pt-20">
      <section className="relative py-20 bg-black overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://readdy.ai/api/search-image?query=abstract%20dark%20technology%20communication%20network%20glowing%20lines%20minimal%20cinematic%20deep%20space%20wide&width=1440&height=400&seq=contacthero&orientation=landscape" alt="Contact background" className="w-full h-full object-cover object-top opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/98 to-black/75" />
        </div>
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#C8F135]/40 to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-[#C8F135]/10 border border-[#C8F135]/20 rounded-full px-4 py-1.5 mb-6">
            <span className="text-[#C8F135] text-xs font-semibold uppercase tracking-wider">Let&apos;s Talk</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-black text-white mb-5">
            Get in <span style={{color: "#C8F135"}}>Touch</span>
          </h1>
          <p className="text-slate-300 text-lg max-w-xl mx-auto">Ready to transform your business? Our team is standing by to help you build something extraordinary.</p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            <div className="lg:col-span-2 flex flex-col gap-8">
              <div>
                <h2 className="text-2xl font-black text-slate-900 mb-3">We&apos;d love to hear from you</h2>
                <p className="text-slate-500 text-sm leading-relaxed">Whether you have a project in mind, a question about our services, or just want to explore possibilities, reach out. We typically respond within 24 hours.</p>
              </div>
              <button onClick={openAIAgent} className="w-full flex items-center gap-4 p-5 rounded-xl border-2 border-[#C8F135]/60 bg-[#C8F135]/5 hover:bg-[#C8F135]/10 hover:border-[#C8F135] transition-all cursor-pointer group text-left">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#C8F135] flex-shrink-0">
                  <i className="ri-robot-2-line text-black text-xl" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-sm font-bold text-slate-900">Chat with our AI</span>
                    <span className="flex items-center gap-1 bg-emerald-100 text-emerald-700 text-xs font-semibold px-2 py-0.5 rounded-full">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />Online
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 leading-snug">Get instant answers about our services, pricing, and more</p>
                </div>
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#C8F135]/10 group-hover:bg-[#C8F135] transition-colors flex-shrink-0">
                  <i className="ri-arrow-right-line text-[#9bc400] group-hover:text-black text-sm transition-colors" />
                </div>
              </button>

              <div className="flex flex-col gap-3">
                {[
                  { icon: "ri-mail-line", label: "Email Us", value: "sales@cpitechinc.com", href: "mailto:sales@cpitechinc.com" },
                  { icon: "ri-phone-line", label: "Call Us", value: "+1 (224) 201-8888", href: "tel:+12242018888" },
                  { icon: "ri-map-pin-line", label: "Visit Us", value: "Chicago, IL, USA", href: "https://www.google.com/maps/place/Chicago,+IL,+USA" },
                  { icon: "ri-time-line", label: "Business Hours", value: "Mon–Fri, 9am–6pm CST", href: "#" },
                ].map((item) => (
                  <a key={item.label} href={item.href} rel="nofollow" target={item.href.startsWith("http") ? "_blank" : undefined}
                    className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl hover:bg-[#C8F135]/10 border border-slate-100 hover:border-[#C8F135]/30 transition-all cursor-pointer group">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#C8F135]/20 flex-shrink-0 group-hover:bg-[#C8F135] transition-colors">
                      <i className={`${item.icon} text-[#9bc400] group-hover:text-black transition-colors`} />
                    </div>
                    <div>
                      <div className="text-xs text-slate-400 font-medium">{item.label}</div>
                      <div className="text-sm font-semibold text-slate-800">{item.value}</div>
                    </div>
                  </a>
                ))}
              </div>

              <div>
                <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider mb-3">Follow Us</p>
                <div className="flex gap-3">
                  {[{ icon: "ri-linkedin-fill" }, { icon: "ri-twitter-x-fill" }, { icon: "ri-github-fill" }, { icon: "ri-instagram-line" }].map((s) => (
                    <a key={s.icon} href="#" rel="nofollow" className="w-9 h-9 flex items-center justify-center rounded-lg bg-slate-100 hover:bg-[#C8F135] hover:text-black text-slate-600 transition-all cursor-pointer">
                      <i className={`${s.icon} text-sm`} />
                    </a>
                  ))}
                </div>
              </div>
              <div className="rounded-xl overflow-hidden w-full h-48 border border-slate-100">
                <iframe title="CPI Tech Chicago Location" src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d190.0!2d-87.6298!3d41.8781!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x880e2c3cd0f4cbed%3A0xafe0a6ad09c0c000!2sChicago%2C%20IL!5e0!3m2!1sen!2sus!4v1700000000000" width="100%" height="100%" style={{ border: 0 }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
              </div>
            </div>

            <div className="lg:col-span-3">
              <div className="bg-black rounded-2xl p-8 md:p-10">
                <h2 className="text-2xl font-black text-white mb-2">Start Your Project</h2>
                <p className="text-zinc-400 text-sm mb-8">Fill out the form and our team will get back to you within 24 hours.</p>
                {submitted ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#C8F135]/20 mb-4">
                      <i className="ri-check-line text-[#C8F135] text-3xl" />
                    </div>
                    <h3 className="text-xl font-black text-white mb-2">Message Sent!</h3>
                    <p className="text-zinc-400 text-sm">Thanks for reaching out. We&apos;ll be in touch within 24 hours.</p>
                  </div>
                ) : (
                  <form id="contact-form" onSubmit={handleSubmit} className="flex flex-col gap-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="text-xs text-zinc-400 font-medium mb-1.5 block">Full Name *</label>
                        <input type="text" name="name" value={form.name} onChange={handleChange} required placeholder="John Smith" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#C8F135] transition-colors" />
                      </div>
                      <div>
                        <label className="text-xs text-zinc-400 font-medium mb-1.5 block">Email Address *</label>
                        <input type="email" name="email" value={form.email} onChange={handleChange} required placeholder="john@company.com" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#C8F135] transition-colors" />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="text-xs text-zinc-400 font-medium mb-1.5 block">Phone Number</label>
                        <input type="tel" name="phone" value={form.phone} onChange={handleChange} placeholder="+1 (555) 000-0000" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#C8F135] transition-colors" />
                      </div>
                      <div>
                        <label className="text-xs text-zinc-400 font-medium mb-1.5 block">Service Needed</label>
                        <select name="service" value={form.service} onChange={handleChange} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-[#C8F135] transition-colors appearance-none cursor-pointer">
                          <option value="" className="bg-zinc-900">Select a service</option>
                          {serviceOptions.map((o) => <option key={o} value={o} className="bg-zinc-900">{o}</option>)}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-zinc-400 font-medium mb-1.5 block">Project Budget</label>
                      <select name="budget" value={form.budget} onChange={handleChange} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-[#C8F135] transition-colors appearance-none cursor-pointer">
                        <option value="" className="bg-zinc-900">Select budget range</option>
                        {budgetOptions.map((o) => <option key={o} value={o} className="bg-zinc-900">{o}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-zinc-400 font-medium mb-1.5 block">Message * <span className="text-zinc-600">({form.message.length}/500)</span></label>
                      <textarea name="message" value={form.message} onChange={handleChange} required maxLength={500} rows={4} placeholder="Tell us about your project, goals, and timeline..." className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#C8F135] transition-colors resize-none" />
                    </div>
                    {error && <p className="text-red-400 text-xs">{error}</p>}
                    <button type="submit" disabled={submitting} className="w-full py-4 rounded-xl bg-[#C8F135] text-black font-bold text-sm whitespace-nowrap cursor-pointer hover:bg-[#b5e000] transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                      {submitting ? <><i className="ri-loader-4-line animate-spin" /> Sending...</> : <><i className="ri-send-plane-line" /> Send Message</>}
                    </button>
                    <p className="text-xs text-zinc-600 text-center">By submitting, you agree to our Privacy Policy. We never share your data.</p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-black text-slate-900">
              Frequently Asked <span style={{color: "#9bc400"}}>Questions</span>
            </h2>
          </div>
          <div className="flex flex-col gap-3">
            {[
              { q: "How quickly can you start on my project?", a: "We typically onboard new clients within 1–2 weeks. For urgent projects, we can often start sooner." },
              { q: "Do you work with startups or only enterprises?", a: "We work with businesses of all sizes, from early-stage startups to Fortune 500 companies. Our solutions scale to fit your needs and budget." },
              { q: "What does a typical engagement look like?", a: "We start with a free discovery call, then deliver a tailored proposal. Once approved, we kick off with a dedicated project manager and weekly check-ins." },
              { q: "Do you offer ongoing support after project delivery?", a: "Absolutely. We offer flexible retainer and support packages to ensure your solution continues to perform and evolve with your business." },
            ].map((faq, i) => <FaqItem key={i} question={faq.q} answer={faq.a} />)}
          </div>
        </div>
      </section>
    </main>
  );
}

function FaqItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white rounded-xl overflow-hidden border border-slate-100">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-6 py-4 text-left cursor-pointer">
        <span className="text-sm font-semibold text-slate-900">{question}</span>
        <i className={`ri-arrow-down-s-line text-slate-400 transition-transform duration-200 flex-shrink-0 ml-4 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-6 pb-5"><p className="text-sm text-slate-500 leading-relaxed">{answer}</p></div>}
    </div>
  );
}
